import { createTheme } from '@mui/material/styles';
import { PaletteColor, PaletteColorOptions } from '@mui/material/styles/createPalette';

// MAYA Research Platform 配色系统
// 依据：MAYA_Research_Platform_Color_System（Black / White / Purple，两套强调方式）
export const MAYA_COLORS = {
    // 共享基础色
    inkBlack: '#15141A',
    warmWhite: '#FAFAFC',
    pureWhite: '#FFFFFF',
    royalPurple: '#6D4CFF',
    deepViolet: '#4F35C9',
    softLavender: '#EDE7FF',
    graphite: '#5E6170',
    borderGray: '#E7E8EF',
    mistGray: '#F1F2F7',
    // 管理端深色
    deepCharcoal: '#13131A',
    softBlack: '#1F2030',
    offWhite: '#F6F7FB',
    adminPurple: '#8B6BFF',
    lavenderAccent: '#B39DFF',
    borderDark: '#343447',
    // 语义色
    success: '#22B573',
    information: '#3E7BFA',
    warning: '#F3A531',
    error: '#E45757',
} as const;

// 字体栈：正文用无衬线（中英混排），展示级标题可选衬线（学术期刊排法）
export const MAYA_FONTS = {
    sans: "'Work Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', 'Helvetica Neue', Arial, sans-serif",
    display:
        "'Iowan Old Style', 'Palatino Linotype', Palatino, Georgia, 'Times New Roman', 'Songti SC', 'Noto Serif SC', 'STSong', SimSun, serif",
} as const;

// 语义色浅底：用于提示条背景（默认 12% 透明）
export const tint = (hex: string, alpha = 0.12) => {
    const value = hex.replace('#', '');
    const red = parseInt(value.slice(0, 2), 16);
    const green = parseInt(value.slice(2, 4), 16);
    const blue = parseInt(value.slice(4, 6), 16);
    return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

declare module '@mui/material/styles' {
    interface Palette {
        assistantMessage?: PaletteColor;
        userMessage?: PaletteColor;
        adminShell?: { main: string; surface: string; border: string; text: string };
        adminAccent?: { main: string; light: string };
        neutral?: { block: string; warm: string };
    }

    interface PaletteOptions {
        assistantMessage?: PaletteColorOptions;
        userMessage?: PaletteColorOptions;
        adminShell?: { main: string; surface: string; border: string; text: string };
        adminAccent?: { main: string; light: string };
        neutral?: { block: string; warm: string };
    }
}

const theme = createTheme({
    palette: {
        primary: {
            main: MAYA_COLORS.royalPurple,
            dark: MAYA_COLORS.deepViolet,
            light: MAYA_COLORS.softLavender,
            contrastText: MAYA_COLORS.pureWhite,
        },
        secondary: {
            main: MAYA_COLORS.deepViolet,
            light: MAYA_COLORS.lavenderAccent,
            dark: '#3A2492',
            contrastText: MAYA_COLORS.pureWhite,
        },
        error: { main: MAYA_COLORS.error },
        warning: { main: MAYA_COLORS.warning },
        info: { main: MAYA_COLORS.information },
        success: { main: MAYA_COLORS.success },
        text: {
            primary: MAYA_COLORS.inkBlack,
            secondary: MAYA_COLORS.graphite,
        },
        background: {
            default: MAYA_COLORS.warmWhite,
            paper: MAYA_COLORS.pureWhite,
        },
        divider: MAYA_COLORS.borderGray,
        // 对话气泡：AI 白底描边、被试浅灰（避免引入新的色相）
        assistantMessage: { main: MAYA_COLORS.pureWhite },
        userMessage: { main: MAYA_COLORS.mistGray },
        // 管理端
        adminShell: {
            main: MAYA_COLORS.deepCharcoal,
            surface: MAYA_COLORS.softBlack,
            border: MAYA_COLORS.borderDark,
            text: MAYA_COLORS.offWhite,
        },
        adminAccent: {
            main: MAYA_COLORS.adminPurple,
            light: MAYA_COLORS.lavenderAccent,
        },
        neutral: {
            block: MAYA_COLORS.mistGray,
            warm: MAYA_COLORS.warmWhite,
        },
    },
    typography: {
        // 中英混排：Work Sans 承担拉丁字母，中文回退到系统高质量黑体
        fontFamily: MAYA_FONTS.sans,
        fontWeightLight: 300,
        fontWeightRegular: 400,
        fontWeightMedium: 500,
        fontWeightBold: 600,
        // 学术式层级：字号跨度克制，标题 500/600，正文 400，行高宽松
        h1: {
            fontSize: '2.75rem',
            fontWeight: 500,
            lineHeight: 1.15,
            letterSpacing: '-0.02em',
        },
        h2: {
            fontSize: '2.125rem',
            fontWeight: 500,
            lineHeight: 1.2,
            letterSpacing: '-0.015em',
        },
        h3: {
            fontSize: '1.625rem',
            fontWeight: 500,
            lineHeight: 1.25,
            letterSpacing: '-0.01em',
        },
        h4: {
            fontSize: '1.375rem',
            fontWeight: 500,
            lineHeight: 1.3,
            letterSpacing: '-0.005em',
        },
        h5: {
            fontSize: '1.125rem',
            fontWeight: 600,
            lineHeight: 1.35,
            letterSpacing: 0,
        },
        h6: {
            fontSize: '1rem',
            fontWeight: 600,
            lineHeight: 1.4,
            letterSpacing: '0.01em',
        },
        subtitle1: {
            fontSize: '1rem',
            fontWeight: 500,
            lineHeight: 1.5,
            letterSpacing: 0,
        },
        subtitle2: {
            fontSize: '0.875rem',
            fontWeight: 600,
            lineHeight: 1.5,
            letterSpacing: '0.01em',
        },
        body1: {
            fontSize: '1rem',
            fontWeight: 400,
            lineHeight: 1.7,
            letterSpacing: '0.003em',
        },
        body2: {
            fontSize: '0.9375rem',
            fontWeight: 400,
            lineHeight: 1.65,
            letterSpacing: '0.003em',
        },
        caption: {
            fontSize: '0.8125rem',
            fontWeight: 400,
            lineHeight: 1.5,
            letterSpacing: '0.01em',
        },
        overline: {
            fontSize: '0.6875rem',
            fontWeight: 600,
            lineHeight: 1.6,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
        },
        button: {
            fontSize: '0.9375rem',
            fontWeight: 500,
            letterSpacing: '0.01em',
            textTransform: 'none',
        },
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontWeight: 500,
                    letterSpacing: '0.01em',
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    fontSize: '0.875rem',
                    letterSpacing: '0.005em',
                    // 数据表格使用等宽数字，便于逐行比对
                    fontVariantNumeric: 'tabular-nums',
                },
                head: {
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    letterSpacing: '0.03em',
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: { fontSize: '0.75rem', fontWeight: 500, letterSpacing: '0.01em' },
                label: { paddingLeft: '10px', paddingRight: '10px' },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: { fontSize: '1.25rem', fontWeight: 600, letterSpacing: '-0.005em' },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: { fontSize: '0.8125rem', lineHeight: 1.5, letterSpacing: '0.01em' },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: { fontSize: '0.8125rem', lineHeight: 1.5, letterSpacing: '0.005em' },
            },
        },
    },
    spacing: 8,
    shape: {
        borderRadius: 8,
    },
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 960,
            lg: 1280,
            xl: 1920,
        },
    },
});

export default theme;
